import FormControl from "common/components/FormControl/FormControl";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Plot, Zone } from "types";
import MapField from "common/components/MapField/MapField";
import CrudForm from "common/components/Crud/CrudForm";
import useList from "hooks/useList";

type Props = {
  onSubmit: (values: any) => any;
  onCancel?: () => void;
  item?: Plot;
};

function PlotForm({ onSubmit, onCancel, item }: Props) {
  const { t } = useTranslation();

  const { items: zones, isFetching: isZonesFetching } = useList<Zone>("zones", {
    defaultItemsPerPage: 1000,
    defaultSort: "name:ASC",
  });

  const zoneOptions = useMemo(
    () => (zones ?? []).map((z) => ({ value: z.id, label: z.name })),
    [zones]
  );

  const initialValues = useMemo(
    () =>
      item?.id
        ? {
            ...item,
            ricePaddy: item.ricePaddy?.id ?? null,
          }
        : {},
    [item]
  );

  const handleSubmit = (payload: any) => {
    const data = payload?.data ?? payload;
    const { geojsonTemp, ...rest } = data;

    return onSubmit({
      ...payload,
      data: {
        ...rest,
        lat: rest.lat ?? geojsonTemp?.lat,
        lng: rest.lng ?? geojsonTemp?.lng,
      },
    });
  };

  return (
    <CrudForm
      onSubmit={handleSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      headerIcon="MapPinIcon"
      i18nNamespace="plots"
      additionalMutators={{
        setPosition: (args: any, state: any, utils: any) => {
          const position = args[0];

          utils.changeValue(state, "lat", () => position.lat);
          utils.changeValue(state, "lng", () => position.lng);
        },
        setHelpPosition: (
          [_, geo]: any,
          state: any,
          utils: {
            changeValue: (
              arg0: any,
              arg1: string,
              arg2: { (): any; (): any }
            ) => void;
          }
        ) => {
          utils.changeValue(
            state,
            "geojsonTemp.lat",
            () => geo && geo.location?.lat()
          );
          utils.changeValue(
            state,
            "geojsonTemp.lng",
            () => geo && geo.location?.lng()
          );
        },
      }}
      render={(values, form) => (
        <div className="flex flex-row flex-wrap gap-4">
          <div className="flex flex-col flex-[2] gap-4 white-box">
            <FormControl name="name" title={t("plots.name")} required />
            <FormControl name="variety" title={t("plots.variety")} />
            <div className="flex flex-row gap-4">
              <FormControl
                name="uniqueID"
                title={t("plots.uniqueID")}
                containerStyle="flex-1"
              />
              <FormControl
                name="mobileID"
                title={t("plots.mobileID")}
                containerStyle="flex-1"
              />
            </div>
            <div className="flex flex-row gap-4">
              <FormControl
                name="ricePaddy"
                title={t("plots.ricepaddy")}
                type="select"
                options={zoneOptions}
                containerStyle="flex-1"
              />
              <FormControl
                name="step"
                title={t("plots.step")}
                type="number"
                containerStyle="flex-1"
              />
            </div>
            <FormControl
              name="stepDate"
              title={t("plots.stepDate")}
              type="datetime"
            />
          </div>
          <div className="flex flex-col flex-1 gap-2 white-box">
            <MapField
              position={{
                lat: values.lat ?? 43.6043,
                lng: values.lng ?? 1.4437,
              }}
              positionTemp={{
                lat: values.geojsonTemp?.lat ?? 43.6043,
                lng: values.geojsonTemp?.lng ?? 1.4437,
              }}
              onChange={form.mutators.setPosition}
              editable
              mapPrefix="plotForm"
              form={form}
            />
          </div>
        </div>
      )}
    />
  );
}

export default PlotForm;
